import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { useApi } from '@/api/apiContext';
import { ApiError } from '@/api/errors';
import {
  collectionsListQueryKey,
  collectionsQueryKey,
  collectionsQueryOptions,
  confirmCollectionMutationOptions,
  correctCollectionMutationOptions,
  moveCollectedRow,
  patchCollectedRow,
} from '@/api/endpoints/collections';
import { groundHandlersQueryOptions } from '@/api/endpoints/groundHandlers';
import { messages } from '@/i18n';
import type { CollectionRow } from '@/types/collection';
import { CollectionsSection } from './CollectionsSection';
import {
  ConfirmCollectionDialog,
  DateFilterInput,
  HandlerFilterSelect,
  PageHeader,
} from './collectionsComponents';

const PAGE_SIZE = 25;

function useCollectionsFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const handlerFilter = searchParams.get('handler') ?? 'all';
  const dateFilter = searchParams.get('clearedDate') ?? '';
  const isHandlerFiltered = handlerFilter !== 'all';
  const isDateFiltered = dateFilter !== '';

  function setFilters(next: { handler?: string; clearedDate?: string }) {
    const nextHandler = next.handler ?? handlerFilter;
    const nextDate = next.clearedDate ?? dateFilter;
    const params: Record<string, string> = {};
    if (nextHandler !== 'all') params.handler = nextHandler;
    if (nextDate) params.clearedDate = nextDate;
    setSearchParams(params);
  }

  return {
    handlerFilter,
    dateFilter,
    isHandlerFiltered,
    isDateFiltered,
    setHandlerFilter: (value: string) => setFilters({ handler: value }),
    setDateFilter: (value: string) => setFilters({ clearedDate: value }),
  };
}

function ReadyCollectionsScreen({ overview }: { overview: boolean }) {
  const t = messages.driverCollections;
  const api = useApi();
  const queryClient = useQueryClient();
  const { handlerFilter, dateFilter, isHandlerFiltered, isDateFiltered, setHandlerFilter, setDateFilter } =
    useCollectionsFilters();
  const [skip, setSkip] = useState(0);

  const [dialogRow, setDialogRow] = useState<CollectionRow | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  // A ref rather than state: the heading only needs focus once, right after
  // the mutation removes this row's card — queueing it as state would mean a
  // second render that changes nothing on screen.
  const focusHeadingPending = useRef(false);

  const params = useMemo(
    () => ({
      status: 'ReadyForCollection' as const,
      handlerId: isHandlerFiltered ? handlerFilter : undefined,
      customsClearedAt: isDateFiltered ? dateFilter : undefined,
      skip,
      take: PAGE_SIZE,
    }),
    [isHandlerFiltered, handlerFilter, isDateFiltered, dateFilter, skip],
  );
  const readyKey = collectionsQueryKey(params);
  const collectedKey = useMemo(
    () =>
      collectionsQueryKey({
        status: 'Collected',
        handlerId: isHandlerFiltered ? handlerFilter : undefined,
        customsClearedAt: isDateFiltered ? dateFilter : undefined,
        skip: 0,
        take: PAGE_SIZE,
      }),
    [isHandlerFiltered, handlerFilter, isDateFiltered, dateFilter],
  );

  const query = useQuery(collectionsQueryOptions(api, params));
  const handlersQuery = useQuery(groundHandlersQueryOptions(api));
  const items = query.data?.items ?? [];
  const handlers = handlersQuery.data ?? [];
  const total = query.data?.total ?? 0;

  function handleSetHandlerFilter(value: string) {
    setHandlerFilter(value);
    setSkip(0);
  }

  function handleSetDateFilter(value: string) {
    setDateFilter(value);
    setSkip(0);
  }

  const confirmMutation = useMutation({
    ...confirmCollectionMutationOptions(api),
    onSuccess: (row) => {
      moveCollectedRow(queryClient, { readyKey, collectedKey }, row);
      // The Collected cache above is written directly, but Collected lives on
      // a different route now and may not be mounted — invalidate the whole
      // list prefix too, so it refetches instead of showing stale data when
      // it next mounts (docs/driver-collections-split-plan.md §3.1 blocker #1).
      void queryClient.invalidateQueries({ queryKey: collectionsListQueryKey });
      setDialogRow(null);
      focusHeadingPending.current = true;
      // The confirmed row leaves the list, so every later offset shifts —
      // see docs/driver-collections-pagination-backend.md §5 for what this
      // does and does not cover.
      setSkip(0);
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) {
        void query.refetch();
        // A conflict here usually means this row's state moved under us (e.g.
        // someone else already confirmed it) — the Collected page may now be
        // stale too, even though it isn't mounted to refetch itself.
        void queryClient.invalidateQueries({ queryKey: collectionsListQueryKey });
      }
    },
  });

  useEffect(() => {
    if (!focusHeadingPending.current) return;
    focusHeadingPending.current = false;
    // The confirmed row leaves this page entirely, so there is no card left
    // to focus — the heading is the closest stable landing spot.
    headingRef.current?.focus();
  });

  return (
    <section className="mx-auto max-w-2xl">
      <PageHeader
        headingRef={headingRef}
        title={overview ? t.titleReadyOverview : t.titleReady}
        subtitle={overview ? t.subtitleReadyOverview : t.subtitleReady}
        count={total}
      />
      <HandlerFilterSelect handlers={handlers} value={handlerFilter} onChange={handleSetHandlerFilter} />
      <DateFilterInput value={dateFilter} onChange={handleSetDateFilter} />

      <CollectionsSection
        items={items}
        isLoading={query.isLoading}
        isError={query.isError}
        emptyMessage={
          isHandlerFiltered
            ? t.emptyReadyFiltered
            : isDateFiltered
              ? t.emptyReadyDateFiltered
              : overview
                ? t.emptyReadyOverview
                : t.emptyReady
        }
        errorMessage={t.loadErrorReady}
        onRetry={() => void query.refetch()}
        onAction={setDialogRow}
        pagination={{ skip, total, pageSize: PAGE_SIZE, onSkipChange: setSkip }}
      />

      {!query.isLoading && items.length === 0 && (isHandlerFiltered || isDateFiltered) ? (
        <div className="mt-4 flex flex-wrap gap-2">
          {isHandlerFiltered ? (
            <Button variant="outline" onClick={() => handleSetHandlerFilter('all')}>
              {t.clearFilter}
            </Button>
          ) : null}
          {isDateFiltered ? (
            <Button variant="outline" onClick={() => handleSetDateFilter('')}>
              {t.clearDateFilter}
            </Button>
          ) : null}
        </div>
      ) : null}

      {dialogRow ? (
        <ConfirmCollectionDialog
          collection={dialogRow}
          mode="confirm"
          pending={confirmMutation.isPending}
          error={confirmMutation.error}
          onClose={() => setDialogRow(null)}
          onSubmitConfirm={(units) =>
            confirmMutation.mutate({ id: dialogRow.id, body: { collectedUnits: units } })
          }
          onSubmitCorrect={() => {}}
        />
      ) : null}
    </section>
  );
}

function CollectedCollectionsScreen({ overview }: { overview: boolean }) {
  const t = messages.driverCollections;
  const api = useApi();
  const queryClient = useQueryClient();
  const { handlerFilter, dateFilter, isHandlerFiltered, isDateFiltered, setHandlerFilter, setDateFilter } =
    useCollectionsFilters();
  const [skip, setSkip] = useState(0);

  const [dialogRow, setDialogRow] = useState<CollectionRow | null>(null);
  const cardNodes = useRef(new Map<string, HTMLDivElement>());
  const pendingFocusId = useRef<string | null>(null);

  const params = useMemo(
    () => ({
      status: 'Collected' as const,
      handlerId: isHandlerFiltered ? handlerFilter : undefined,
      customsClearedAt: isDateFiltered ? dateFilter : undefined,
      skip,
      take: PAGE_SIZE,
    }),
    [isHandlerFiltered, handlerFilter, isDateFiltered, dateFilter, skip],
  );
  const collectedKey = collectionsQueryKey(params);

  const query = useQuery(collectionsQueryOptions(api, params));
  const handlersQuery = useQuery(groundHandlersQueryOptions(api));
  const items = query.data?.items ?? [];
  const handlers = handlersQuery.data ?? [];
  const total = query.data?.total ?? 0;

  function handleSetHandlerFilter(value: string) {
    setHandlerFilter(value);
    setSkip(0);
  }

  function handleSetDateFilter(value: string) {
    setDateFilter(value);
    setSkip(0);
  }

  const correctMutation = useMutation({
    ...correctCollectionMutationOptions(api),
    onSuccess: (row) => {
      patchCollectedRow(queryClient, collectedKey, row);
      setDialogRow(null);
      pendingFocusId.current = row.id;
    },
    onError: (error) => {
      if (error instanceof ApiError) void query.refetch();
    },
  });

  useEffect(() => {
    const id = pendingFocusId.current;
    if (!id) return;
    pendingFocusId.current = null;
    cardNodes.current.get(id)?.focus();
  });

  function cardRefFor(id: string) {
    return (node: HTMLDivElement | null) => {
      if (node) cardNodes.current.set(id, node);
      else cardNodes.current.delete(id);
    };
  }

  return (
    <section className="mx-auto max-w-2xl">
      <PageHeader
        title={overview ? t.titleCollectedOverview : t.titleCollected}
        subtitle={overview ? t.subtitleCollectedOverview : t.subtitleCollected}
        count={total}
      />
      <p className="text-muted-foreground -mt-4 mb-6 text-xs">{t.collectedWindowHint}</p>
      <HandlerFilterSelect handlers={handlers} value={handlerFilter} onChange={handleSetHandlerFilter} />
      <DateFilterInput value={dateFilter} onChange={handleSetDateFilter} />

      <CollectionsSection
        items={items}
        isLoading={query.isLoading}
        isError={query.isError}
        emptyMessage={
          isHandlerFiltered
            ? t.emptyCollectedFiltered
            : isDateFiltered
              ? t.emptyCollectedDateFiltered
              : t.emptyCollected
        }
        errorMessage={t.loadErrorCollected}
        onRetry={() => void query.refetch()}
        onAction={setDialogRow}
        cardRefFor={cardRefFor}
        pagination={{ skip, total, pageSize: PAGE_SIZE, onSkipChange: setSkip }}
      />

      {!query.isLoading && items.length === 0 && (isHandlerFiltered || isDateFiltered) ? (
        <div className="mt-4 flex flex-wrap gap-2">
          {isHandlerFiltered ? (
            <Button variant="outline" onClick={() => handleSetHandlerFilter('all')}>
              {t.clearFilter}
            </Button>
          ) : null}
          {isDateFiltered ? (
            <Button variant="outline" onClick={() => handleSetDateFilter('')}>
              {t.clearDateFilter}
            </Button>
          ) : null}
        </div>
      ) : null}

      {dialogRow ? (
        <ConfirmCollectionDialog
          collection={dialogRow}
          mode="correct"
          pending={correctMutation.isPending}
          error={correctMutation.error}
          onClose={() => setDialogRow(null)}
          onSubmitConfirm={() => {}}
          onSubmitCorrect={(units, reason) =>
            correctMutation.mutate({ id: dialogRow.id, body: { collectedUnits: units, reason } })
          }
        />
      ) : null}
    </section>
  );
}

export function DriverCollectionsScreen({
  variant,
  overview = false,
}: {
  variant: 'ready' | 'collected';
  overview?: boolean;
}) {
  return variant === 'ready' ? (
    <ReadyCollectionsScreen overview={overview} />
  ) : (
    <CollectedCollectionsScreen overview={overview} />
  );
}
